import React from 'react'
import { TableRow } from '@mui/material'
import SummaryCell from './SummaryCell'
import {
    formatNumber,
    formatPercentageNonRoundUptoTwo,
} from '../../../../../utils/helpers'

interface ColumnConfig {
    field: string
    isSticky?: boolean
    leftOffset?: number
    rightOffset?: number
    hasBorder?: boolean
}

interface SummaryRowsProps {
    row: any
    index: number
    modifiedRows: any[]
    selectedYear: string
    computedLeftOffsets: Record<number, number>
    computedRightOffsets: Record<number, number>
    getQuarterData: (index: number) => any
    getYearData: () => any
    columnMapping: ColumnConfig[]
}

const SummaryRows: React.FC<SummaryRowsProps> = ({
    row,
    index,
    modifiedRows,
    selectedYear,
    getQuarterData,
    getYearData,
    columnMapping,
}) => {
    const isLastMonthOfQuarter = [
        'March',
        'June',
        'September',
        'December',
    ].includes(row.displayMonth)
    const isLastRowOfYear = index === modifiedRows.length - 1
    const quarterData = isLastMonthOfQuarter ? getQuarterData(index) : null
    const yearData = isLastRowOfYear ? getYearData() : null

    const renderCellValue = (field: string, data: any) => {
        if (!data) return null

        switch (field) {
            case 'month':
                return null // Month label is handled separately
            case 'ftd':
                return formatNumber(Number(data.ftd.toFixed(0)))
            case 'profit':
                return `${formatNumber(Number(data.profit.toFixed(0)))}$`
            case 'avgGrowth':
                return formatPercentageNonRoundUptoTwo(data.avgGrowth)
            case 'netRevenue':
                return `${formatNumber(Number(data.netRevenue.toFixed(0)))}$`
            case 'rs':
                return `${formatNumber(Number(data.rs.toFixed(0)))}$`
            case 'cpaIncome':
                return `${formatNumber(Number(data.cpaIncome.toFixed(0)))}$`
            case 'totalIncome':
                return `${formatNumber(Number(data.totalIncome.toFixed(0)))}$`
            case 'spend':
                return `${formatNumber(Number(data.spend.toFixed(0)))}$`
            case 'CAC':
                return `${formatNumber(Number(data.CAC.toFixed(0)))}$`
            case "clicks":
                return `${formatNumber(Number(data.clicks.toFixed(0)))}`
            default:
                return null
        }
    }

    const getQuarterLabel = () => {
        if (row.displayMonth === 'March') return 'Q1 Total'
        if (row.displayMonth === 'June') return 'Q2 Total'
        if (row.displayMonth === 'September') return 'Q3 Total'
        return 'Q4 Total'
    }

    return (
        <>
            {/* Quarterly Summary Row */}
            {isLastMonthOfQuarter && quarterData && (
                <TableRow
                    sx={{
                        backgroundColor: 'transparent',
                        '& > *': {
                            borderBottom: '2px solid #E7E7E7 !important',
                        },
                        height: 35,
                    }}
                >
                    {columnMapping.map((config, idx) => {
                        // First column is always the label
                        if (idx === 0) {
                            return (
                                <SummaryCell
                                    key={idx}
                                    value={getQuarterLabel()}
                                    isSticky={config.isSticky}
                                    leftOffset={config.leftOffset}
                                    rightOffset={config.rightOffset}
                                    fontWeight="medium"
                                />
                            )
                        }

                        return (
                            <SummaryCell
                                key={idx}
                                value={renderCellValue(
                                    config.field,
                                    quarterData,
                                )}
                                isSticky={config.isSticky}
                                leftOffset={config.leftOffset}
                                rightOffset={config.rightOffset}
                                hasBorder={config.hasBorder}
                            />
                        )
                    })}
                </TableRow>
            )}

            {/* Total Year Row */}
            {isLastRowOfYear && yearData && (
                <TableRow
                    sx={{
                        backgroundColor: '#FFF3E0',
                        '& > *': {
                            borderBottom: '3px solid #E7E7E7 !important',
                        },
                        height: 35,
                    }}
                >
                    {columnMapping.map((config, idx) => {
                        // First column is always the label
                        if (idx === 0) {
                            return (
                                <SummaryCell
                                    key={idx}
                                    value={`${selectedYear} Total`}
                                    isSticky={config.isSticky}
                                    leftOffset={config.leftOffset}
                                    rightOffset={config.rightOffset}
                                    fontWeight="medium"
                                />
                            )
                        }

                        return (
                            <SummaryCell
                                key={idx}
                                value={renderCellValue(config.field, yearData)}
                                isSticky={config.isSticky}
                                leftOffset={config.leftOffset}
                                rightOffset={config.rightOffset}
                                hasBorder={config.hasBorder}
                            />
                        )
                    })}
                </TableRow>
            )}
        </>
    )
}

export default SummaryRows
